import React, { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc } from 'firebase/firestore'; 
import { db, functions } from '../../firebase/config';
import { Zap, CheckCircle, Calculator, Save, Loader2, AlertCircle } from 'lucide-react';
import { resolveQuickBooksHealth } from '../../utils/formatters'; // qbhealth-ui-9d1a6f34

// qbsync-5e1a7c34 -- Seam A: the three automatic-invoice-sync timing values plus the
// full copy shown when each is chosen. The on_send copy is verbatim from the plan --
// the emailed-only caveat must be stated plainly at the moment of choosing; the
// on_create copy states it fires at creation only (a draft finalised later won't sync
// retroactively). Read helper matches the backend fallback: absent/invalid -> manual.
const QB_SYNC_TIMINGS = ['on_create', 'on_send', 'manual'];
const QB_SYNC_TIMING_COPY = {
    on_create: "Syncs the moment an invoice is created. This fires at creation only — an invoice saved as a draft and finalised later will not sync retroactively; email it or push it with the Export button.",
    on_send: "Syncs when you email an invoice to the customer. Invoices you don't email (e.g. printed or posted) won't sync automatically — push those with the Export button.",
    manual: "Nothing syncs automatically. Push invoices to QuickBooks yourself with the Export button whenever you're ready.",
};

const IntegrationsPage = ({ user, currentUserProfile, companySettings, isOutlookConnected, isMsCalendarConnected }) => {
    const [message, setMessage] = useState('');
    const [isConnecting, setIsConnecting] = useState(null);
    const [isQuickbooksConnected, setIsQuickbooksConnected] = useState(false);

    // qbcredit-3e7a9c21 -- Seam 6: account-credit backlog choice. qbBacklog holds the
    // connect-time summary {count,totalValue} that opens the choice modal; qbSettingsBacklog
    // holds the fuller settings-path summary {count,totalValue,earliest,latest} shown with a
    // double-count warning BEFORE the operator confirms posting an older/larger backlog.
    const [qbBacklog, setQbBacklog] = useState(null);
    const [qbSettingsBacklog, setQbSettingsBacklog] = useState(null);
    const [qbBacklogBusy, setQbBacklogBusy] = useState(false);

    // --- REFS FOR OPTIMISTIC UI HANDLING ---
    const microsoftExpectDisconnect = useRef(false);

    // --- LOCAL STATE FOR IMMEDIATE UI UPDATES ---
    const [localMicrosoftState, setLocalMicrosoftState] = useState({ email: isOutlookConnected, calendar: isMsCalendarConnected });

    // Sync local state if props change
    useEffect(() => {
        let shouldUpdateMicrosoft = true;
        if (microsoftExpectDisconnect.current) {
            if (isOutlookConnected || isMsCalendarConnected) {
                shouldUpdateMicrosoft = false;
            } else {
                microsoftExpectDisconnect.current = false;
            }
        }
        if (shouldUpdateMicrosoft) {
            setLocalMicrosoftState({ email: isOutlookConnected, calendar: isMsCalendarConnected });
        }
    }, [isOutlookConnected, isMsCalendarConnected]);

    // --- CONNECTED STATUS HELPERS ---
    const isMicrosoftConnectedState = localMicrosoftState.email || localMicrosoftState.calendar;

    // --- NEW STATE FOR QUICKBOOKS CONFIGURATION ---
    const [qbOptions, setQbOptions] = useState({ accounts: [], taxCodes: [] });
    // d2bank-9c2e5d81 -- Seam D2: the refund-from picker's account list, held separately
    // from qbOptions because it comes from getQuickBooksBankAccounts (shape {id,name,type})
    // and is filtered to real Bank accounts at fetch time (see the fetch below).
    const [qbBankAccounts, setQbBankAccounts] = useState([]);
    const [qbSettings, setQbSettings] = useState({
        incomeAccountId: '',
        materialAccountId: '',
        taxCodeId: '',
        untaxedTaxCodeId: '', // qbuntaxed-8d3f5b27 -- the QuickBooks code untaxed invoices (and Tax Exempt lines) are sent on
        refundFromAccountId: '' // d2bank-9c2e5d81 -- Seam D2 refund-from bank account (optional)
    });
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const [isSavingSettings, setIsSavingSettings] = useState(false);
    const [isTestingQb, setIsTestingQb] = useState(false); // qbhealth-ui-9d1a6f34

    // googleoff-7d2e4a19 -- Google connect/disconnect removed (Google connection is out of
    // the product). The Google<->Microsoft mutual-exclusion lock went with it: a user with
    // stale Google flags must never find Microsoft greyed out with no Google button to clear it.

    // --- 2. MICROSOFT LOGIC ---
    const handleMicrosoftConnect = () => {
        setIsConnecting('microsoft');
        sessionStorage.setItem('pending_oauth', 'microsoft');
        const MICROSOFT_CLIENT_ID = "284ed566-9d31-431d-9366-3f9bbe11954d";
        const REDIRECT_URI = `${window.location.origin}/settings`;
        const scope = "User.Read Mail.Send Calendars.ReadWrite offline_access";
        const url = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
            `client_id=${MICROSOFT_CLIENT_ID}` +
            `&response_type=code` +
            `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
            `&response_mode=query` +
            `&scope=${encodeURIComponent(scope)}` +
            `&prompt=consent`;
        window.location.href = url;
    };

    const handleMicrosoftDisconnect = async () => {
        if (!window.confirm("Are you sure? This will disconnect both Outlook and Calendar.")) return;
        
        setIsConnecting('disconnecting_microsoft');
        microsoftExpectDisconnect.current = true;

        try {
            const disconnectMicrosoft = httpsCallable(functions, 'disconnectMicrosoft');
            const result = await disconnectMicrosoft();

             if (user?.uid) {
                // mailtok-2c7f9e51 — microsoftAccessToken / microsoftRefreshToken were REMOVED from
                // this write. The disconnectMicrosoft callable above already deletes all three token
                // fields with the Admin SDK; this was a redundant client echo, and the new rules
                // guard refuses any client write to a token field — leaving it here would make
                // Disconnect report "Failed to disconnect" even though the disconnect had worked.
                await updateDoc(doc(db, 'users', user.uid), {
                    outlookConnected: false,
                    microsoftCalendarConnected: false,
                    microsoftTokenExpiresAt: null
                });
            }

            setLocalMicrosoftState({ email: false, calendar: false });
            setMessage(result.data.message);
        } catch (error) {
             console.error("Microsoft Disconnect Error:", error);
             setMessage(`Failed to disconnect: ${error.message}`);
             microsoftExpectDisconnect.current = false;
         } finally {
            setIsConnecting(null);
         }
    };

    // --- 3. QUICKBOOKS LOGIC ---
    const handleQuickbooksConnect = () => {
        setIsConnecting('quickbooks');
        sessionStorage.setItem('pending_oauth', 'quickbooks');
        const CLIENT_ID = "ABknSfAggsrbzicNIW2KRnsKvQdVECMJjbzTHNoRo7GmlSfDdb"; 
        const REDIRECT_URI = `${window.location.origin}/settings`;
        const scope = "com.intuit.quickbooks.accounting"; 
        const state = "security_token"; 

        const url = `https://appcenter.intuit.com/connect/oauth2?` +
            `client_id=${CLIENT_ID}` +
            `&response_type=code` +
            `&scope=${scope}` +
            `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
            `&state=${state}`;
        window.location.href = url;
    };

    const handleQuickbooksDisconnect = async () => {
        if (!window.confirm("Are you sure? This will stop syncing Invoices, Payments, and Expenses to QuickBooks.")) return;
        
        setIsConnecting('disconnecting_qb');
        try {
            const disconnectQuickbooks = httpsCallable(functions, 'disconnectQuickbooks');
            await disconnectQuickbooks();
            setMessage('QuickBooks disconnected successfully.');
            setIsQuickbooksConnected(false);
            setQbOptions({ accounts: [], taxCodes: [] }); 
        } catch (error) {
            console.error("QB Disconnect Error:", error);
            setMessage(`Failed to disconnect: ${error.message}`);
        } finally {
            setIsConnecting(null);
        }
    };

    // qbhealth-ui-9d1a6f34 -- on-demand health check. Calls the testQuickBooksConnection
    // callable (no args) and reports its { status } in the shared message banner, matching
    // the try/catch + spinner + banner pattern handleSaveQBSettings uses. The callable
    // itself writes the health fields on the org doc, which flow back through the
    // companySettings snapshot and re-render the pill; this handler only surfaces the result.
    const handleTestQuickBooks = async () => {
        setIsTestingQb(true);
        setMessage('');
        try {
            const testConnection = httpsCallable(functions, 'testQuickBooksConnection');
            const result = await testConnection();
            const status = result?.data?.status;
            if (status === 'healthy') {
                setMessage('QuickBooks connection is healthy.');
            } else if (status === 'auth_failed') {
                setMessage('QuickBooks connection needs attention — please reconnect.');
            } else {
                setMessage("Couldn't verify right now — please try again.");
            }
        } catch (error) {
            console.error("QB Health Check Error:", error);
            setMessage("Couldn't verify right now — please try again.");
        } finally {
            setIsTestingQb(false);
        }
    };

    // --- FETCH QB OPTIONS & HANDLE SETTINGS SAVE ---
    const isQBConnected = isQuickbooksConnected || (companySettings?.quickbooksConnected === true);
    const isAdmin = currentUserProfile?.role === 'Admin' || currentUserProfile?.role === 'Owner/System Admin';

    // qbsync-5e1a7c34 -- Seam A: effective sync timing + the accounting-config gate.
    // orgId from the profile (DepositSettings precedent). qbSyncTiming falls back to
    // 'manual' when the org field is absent or invalid, so a legacy org reads as manual
    // (never blank, never a wrong default). qbAccountsConfigured reads the SAVED org
    // settings -- the exact fields the backend trigger checks -- not the unsaved form
    // state, so the gate matches what would actually happen on an automatic push.
    const orgId = currentUserProfile?.organizationId;
    const qbSyncTiming = QB_SYNC_TIMINGS.includes(companySettings?.quickbooksSyncTiming) ? companySettings.quickbooksSyncTiming : 'manual';
    const qbAccountsConfigured = !!(companySettings?.quickbooksSettings?.defaultIncomeAccountId && companySettings?.quickbooksSettings?.defaultMaterialAccountId);

    // qbhealth-ui-9d1a6f34 -- derive the five-state health display from the live org
    // snapshot. Merge the optimistic local connect flag so a just-connected org shows
    // "Connected — not yet verified" during the brief window before the snapshot updates,
    // instead of flashing the disconnected Connect button (this only ever forces connected
    // TRUE, never false, so a disconnect still resolves correctly from the snapshot).
    const qbHealthOrg = isQuickbooksConnected
        ? { ...(companySettings || {}), quickbooksConnected: true }
        : companySettings;
    const qbHealth = resolveQuickBooksHealth(qbHealthOrg);
    const QB_PILL_TONE = {
        green: 'text-green-700 bg-green-100/50',
        gray: 'text-gray-600 bg-gray-100',
        red: 'text-red-700 bg-red-100/50',
        amber: 'text-amber-700 bg-amber-100/50',
    };

    useEffect(() => {
        if (isQBConnected && isAdmin) {
            if (companySettings?.quickbooksSettings) {
                setQbSettings({
                    incomeAccountId: companySettings.quickbooksSettings.defaultIncomeAccountId || '',
                    materialAccountId: companySettings.quickbooksSettings.defaultMaterialAccountId || '',
                    taxCodeId: companySettings.quickbooksSettings.defaultTaxCodeId || '',
                    untaxedTaxCodeId: companySettings.quickbooksSettings.untaxedTaxCodeId || '', // qbuntaxed-8d3f5b27
                    refundFromAccountId: companySettings.quickbooksSettings.refundFromAccountId || '' // d2bank-9c2e5d81
                });
            }

            const fetchOptions = async () => {
                setIsLoadingOptions(true);
                try {
                    const getOptions = httpsCallable(functions, 'getQuickBooksMappingOptions');
                    const result = await getOptions();
                    setQbOptions(result.data);

                    // d2bank-9c2e5d81 -- Seam D2: load the refund-from candidates. Reuse the
                    // existing getQuickBooksBankAccounts callable (returns {id,name,type}), which
                    // also returns Credit Card and Other Current Asset rows -- and Undeposited
                    // Funds is itself an Other Current Asset, the very account whose rejection
                    // caused this work. Filter to type === 'Bank' HERE so the picker can only ever
                    // offer real bank accounts and cannot reproduce that failure.
                    const getBankAccounts = httpsCallable(functions, 'getQuickBooksBankAccounts');
                    const bankResult = await getBankAccounts();
                    setQbBankAccounts((bankResult.data?.accounts || []).filter(acc => acc.type === 'Bank'));
                } catch (error) {
                    console.error("Failed to load QB options:", error);
                } finally {
                    setIsLoadingOptions(false);
                }
            };

            fetchOptions();
        }
    }, [isQBConnected, isAdmin, companySettings?.quickbooksSettings]); 

    const handleSaveQBSettings = async () => {
        if (!qbSettings.incomeAccountId || !qbSettings.materialAccountId) {
            setMessage("Please select both a Service and a Material Income Account.");
            return;
        }
        // qbuntaxed-8d3f5b27 -- a default tax code is required: invoices can't sync without one.
        if (!qbSettings.taxCodeId) {
            setMessage("Please select a Default Tax Code. Invoices can't be synced to QuickBooks without one.");
            return;
        }

        setIsSavingSettings(true);
        try {
            const saveSettings = httpsCallable(functions, 'saveQuickBooksSettings');
            await saveSettings({
                incomeAccountId: qbSettings.incomeAccountId,
                materialAccountId: qbSettings.materialAccountId,
                taxCodeId: qbSettings.taxCodeId,
                untaxedTaxCodeId: qbSettings.untaxedTaxCodeId || '', // qbuntaxed-8d3f5b27 -- '' clears it
                refundFromAccountId: qbSettings.refundFromAccountId // d2bank-9c2e5d81 -- Seam D2: sent alongside the existing fields so the whole-map save preserves them
            });
            setMessage("QuickBooks settings saved successfully!");
        } catch (error) {
            console.error("Error saving settings:", error);
            setMessage(`Failed to save settings: ${error.message}`);
        } finally {
            setIsSavingSettings(false);
        }
    };

    // qbsync-5e1a7c34 -- write the timing straight to the org doc (DepositSettings
    // precedent: a bare top-level enum, direct updateDoc, no callable). The value reads
    // back live through the companySettings snapshot, so there's no local mirror state to
    // keep in sync -- the select is driven off the snapshot.
    const handleSyncTimingChange = async (value) => {
        if (!orgId) return;
        try {
            await updateDoc(doc(db, 'organizations', orgId), { quickbooksSyncTiming: value });
            setMessage('Invoice sync setting saved.');
        } catch (error) {
            console.error('Error saving sync timing:', error);
            setMessage(`Failed to save sync setting: ${error.message}`);
        }
    };

    // --- 4. AUTH CALLBACK HANDLER ---
    useEffect(() => {
        const handleAuthCallback = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('code');
            const realmId = urlParams.get('realmId'); 
            const error = urlParams.get('error');
            const pendingProvider = sessionStorage.getItem('pending_oauth');

            if (!code || !pendingProvider) {
                return;
            }

            sessionStorage.removeItem('pending_oauth');
            window.history.replaceState({}, document.title, window.location.pathname);

            if (error) {
                setMessage(`${pendingProvider} connection failed: ${urlParams.get('error_description') || error}`);
                setIsConnecting(null);
                return;
            }

            try {
                const redirectUri = `${window.location.origin}/settings`;

                if (pendingProvider === 'microsoft') {
                    setMessage('Finalizing Microsoft connection...');
                    setIsConnecting('microsoft');
                    const microsoftAuthCallback = httpsCallable(functions, 'microsoftAuthCallback');
                    await microsoftAuthCallback({ code, redirectUri });
                    setLocalMicrosoftState({ email: true, calendar: true }); 
                    setMessage('Successfully connected Microsoft!');
                } 
                else if (pendingProvider === 'quickbooks') {
                    setMessage('Finalizing QuickBooks connection...');
                    setIsConnecting('quickbooks');
                    const quickbooksAuthCallback = httpsCallable(functions, 'quickbooksAuthCallback');
                    const cbRes = await quickbooksAuthCallback({ code, redirectUri, realmId });
                    setMessage('Successfully connected QuickBooks!');
                    setIsQuickbooksConnected(true);
                    // Seam 6: if there is an account-credit backlog recorded before this
                    // connect, open the post-vs-forward-only choice.
                    const backlog = cbRes?.data?.deferredBacklog;
                    if (backlog && backlog.count > 0) setQbBacklog(backlog);
                }
                
            } catch (err) {
                setMessage(`Connection failed: ${err.message}`);
                console.error("Auth Callback Error:", err);
            } finally {
                setIsConnecting(null);
            }
        };

        handleAuthCallback();
    }, []);

    // Seam 6: record the backlog choice. 'post' drains the deferred backlog; 'forwardOnly'
    // skips it permanently (credits keep working in Spatchy). Both are reversible from the
    // settings control below.
    const chooseBacklogPolicy = async (policy) => {
        setQbBacklogBusy(true);
        try {
            const fn = httpsCallable(functions, 'setQuickBooksCreditBacklogPolicy');
            await fn({ policy });
            setMessage(policy === 'post'
                ? 'Your earlier account credits will be sent to QuickBooks shortly.'
                : 'Only account credits created from now on will sync to QuickBooks.');
            setQbBacklog(null);
            setQbSettingsBacklog(null);
        } catch (e) {
            setMessage(`Couldn't save that choice: ${e.message}`);
        } finally {
            setQbBacklogBusy(false);
        }
    };

    // Settings path: fetch the fuller summary (count, total, date range) to show the
    // double-count warning BEFORE the operator confirms posting an older/larger backlog.
    const previewBacklog = async () => {
        setQbBacklogBusy(true);
        try {
            const fn = httpsCallable(functions, 'setQuickBooksCreditBacklogPolicy');
            const res = await fn({ preview: true });
            setQbSettingsBacklog(res.data?.summary || { count: 0, totalValue: 0, earliest: null, latest: null, deferred: { count: 0 }, skipped: { count: 0 } });
        } catch (e) {
            setMessage(`Couldn't load the backlog: ${e.message}`);
        } finally {
            setQbBacklogBusy(false);
        }
    };

    const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            {/* Seam 6: connect-time account-credit backlog choice modal. */}
            {qbBacklog && qbBacklog.count > 0 && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-2">Send earlier account credits to QuickBooks?</h3>
                        <p className="text-sm text-gray-600">
                            You have <span className="font-semibold">{qbBacklog.count}</span> account
                            {qbBacklog.count === 1 ? ' credit' : ' credits'} totalling <span className="font-semibold">{money(qbBacklog.totalValue)}</span> recorded
                            in Spatchy before you connected QuickBooks.
                        </p>
                        <p className="text-sm text-gray-600 mt-2">
                            Posting them creates {qbBacklog.count === 1 ? 'a new Payment' : `${qbBacklog.count} new Payments`} in
                            QuickBooks. If any of that money was already entered in QuickBooks by hand, it would then appear twice —
                            and Spatchy can't detect that.
                        </p>
                        <div className="mt-5 flex flex-col sm:flex-row gap-2">
                            <button
                                onClick={() => chooseBacklogPolicy('post')}
                                disabled={qbBacklogBusy}
                                className="flex-1 px-4 py-2 bg-[#2ca01c] text-white text-sm font-bold rounded-lg hover:bg-[#238216] disabled:opacity-50"
                            >
                                {qbBacklogBusy ? 'Saving…' : 'Post existing credits'}
                            </button>
                            <button
                                onClick={() => chooseBacklogPolicy('forwardOnly')}
                                disabled={qbBacklogBusy}
                                className="flex-1 px-4 py-2 text-gray-700 text-sm font-medium border border-gray-200 hover:bg-gray-50 rounded-lg disabled:opacity-50"
                            >
                                Sync only from here forward
                            </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-3">You can change this later in Settings → Integrations.</p>
                    </div>
                </div>
            )}
            <h2 className="text-xl font-bold mb-4">Integrations</h2>
            <div className="space-y-6">
                
                {/* googleoff-7d2e4a19 -- Google Workspace card removed (Google connection is out of the product) */}

                {/* 2. Microsoft Card (Mobile Optimized) */}
                <div className={`border p-4 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${isMicrosoftConnectedState ? 'bg-green-50/30' : ''}`}>
                    <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-full shrink-0 ${isMicrosoftConnectedState ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                            <Zap size={20} />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-800">Microsoft 365</h3>
                            <p className="text-sm text-gray-500">
                                {isMicrosoftConnectedState ? 'Outlook and Calendar are synced.' : 'Connect Outlook and Calendar.'}
                            </p>
                        </div>
                    </div>
                    {isMicrosoftConnectedState ? (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
                            <div className="flex items-center gap-2 text-green-700 bg-green-100/50 px-3 py-1 rounded-full w-fit self-start sm:self-auto">
                                <CheckCircle size={16} /> <span className="text-sm font-medium">Connected</span>
                            </div>
                            <button 
                                onClick={handleMicrosoftDisconnect} 
                                disabled={isConnecting === 'disconnecting_microsoft'}
                                className="w-full sm:w-auto px-4 py-2 text-red-600 text-sm font-medium border border-red-100 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            >
                                {isConnecting === 'disconnecting_microsoft' ? 'Disconnecting...' : 'Disconnect'}
                            </button>
                        </div>
                    ) : (
                        <button 
                            onClick={handleMicrosoftConnect} 
                            disabled={isConnecting}
                            className="w-full sm:w-auto px-4 py-2 border text-sm font-bold rounded-lg transition-colors shadow-sm bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                            {isConnecting === 'microsoft' ? 'Connecting...' : 'Connect'}
                        </button>
                    )}
                </div>

                {/* 3. QuickBooks Card (Mobile Optimized Header) */}
                <div className={`border p-4 rounded-lg ${isQBConnected ? 'bg-green-50/30' : ''}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className={`p-2 rounded-full shrink-0 ${isQBConnected ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                                <Calculator size={20} />
                            </div>
                            {/* qbhealth-ui-9d1a6f34 -- header copy + health sub-line + broken reason line */}
                            <div>
                                <h3 className="font-semibold text-gray-800">QuickBooks Online</h3>
                                <p className="text-sm text-gray-500">
                                    {(qbHealth.state === 'disconnected' || qbHealth.state === 'broken')
                                        ? 'Sync Invoices, Payments, and Expenses.'
                                        : 'Accounting data is synced.'}
                                </p>
                                {qbHealth.subLine && (
                                    <p className="text-xs text-gray-500 mt-1">{qbHealth.subLine}</p>
                                )}
                                {qbHealth.reason && (
                                    <p className="text-xs text-red-600 flex items-start gap-1 mt-1">
                                        <AlertCircle size={12} className="shrink-0 mt-0.5" /> {qbHealth.reason}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* qbhealth-ui-9d1a6f34 -- five-state status block: pill + state-appropriate actions */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
                            {qbHealth.label && (
                                <div className={`flex items-center gap-2 px-3 py-1 rounded-full w-fit self-start sm:self-auto ${QB_PILL_TONE[qbHealth.tone] || 'text-gray-600 bg-gray-100'}`}>
                                    {(qbHealth.tone === 'red' || qbHealth.tone === 'amber')
                                        ? <AlertCircle size={16} />
                                        : <CheckCircle size={16} />}
                                    <span className="text-sm font-medium">{qbHealth.label}</span>
                                </div>
                            )}

                            {qbHealth.state === 'disconnected' ? (
                                isAdmin ? (
                                    <button
                                        onClick={handleQuickbooksConnect}
                                        disabled={isConnecting}
                                        className="w-full sm:w-auto px-4 py-2 bg-[#2ca01c] text-white text-sm font-bold rounded-full hover:bg-[#238216] transition-colors shadow-sm"
                                    >
                                        {isConnecting === 'quickbooks' ? 'Connecting...' : 'Connect QuickBooks'}
                                    </button>
                                ) : (
                                    <span className="text-xs text-gray-400 italic bg-gray-50 px-2 py-1 rounded border">Admin Access Required</span>
                                )
                            ) : isAdmin ? (
                                <>
                                    {qbHealth.state === 'broken' ? (
                                        <button
                                            onClick={handleQuickbooksConnect}
                                            disabled={isConnecting}
                                            className="w-full sm:w-auto px-4 py-2 bg-[#2ca01c] text-white text-sm font-bold rounded-full hover:bg-[#238216] transition-colors shadow-sm"
                                        >
                                            {isConnecting === 'quickbooks' ? 'Reconnecting...' : 'Reconnect QuickBooks'}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleTestQuickBooks}
                                            disabled={isTestingQb}
                                            className="w-full sm:w-auto px-4 py-2 text-gray-700 text-sm font-medium border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                        >
                                            {isTestingQb
                                                ? (<><Loader2 className="animate-spin" size={16} /> Checking…</>)
                                                : 'Test connection'}
                                        </button>
                                    )}
                                    {companySettings?.quickbooksConnected === true && (
                                        <button
                                            onClick={handleQuickbooksDisconnect}
                                            disabled={isConnecting === 'disconnecting_qb'}
                                            className="w-full sm:w-auto px-4 py-2 text-red-600 text-sm font-medium border border-red-100 hover:bg-red-50 rounded-lg transition-colors"
                                        >
                                            {isConnecting === 'disconnecting_qb' ? '...' : 'Disconnect'}
                                        </button>
                                    )}
                                </>
                            ) : null}
                        </div>
                    </div>

                    {/* --- CONFIGURATION SECTION --- */}
                    {isQBConnected && isAdmin && (
                        <div className="mt-6 border-t border-green-200 pt-4">
                            <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <Calculator size={16} /> Accounting Settings
                            </h4>

                            {/* Seam 6: settings-path backlog control. Shows the fuller summary
                                (count, total, date range) + a double-count warning BEFORE the operator
                                confirms posting an older/larger backlog. */}
                            <div className="mb-4 border border-gray-200 rounded-lg p-3">
                                <p className="text-xs font-medium text-gray-700">Earlier account credits</p>
                                {!qbSettingsBacklog ? (
                                    <div className="mt-1 flex items-center justify-between gap-3">
                                        <p className="text-xs text-gray-500">
                                            Account credits recorded before QuickBooks was connected can be sent to QuickBooks now.
                                        </p>
                                        <button
                                            onClick={previewBacklog}
                                            disabled={qbBacklogBusy}
                                            className="shrink-0 px-3 py-1.5 text-xs font-medium border border-gray-200 hover:bg-gray-50 rounded-lg disabled:opacity-50"
                                        >
                                            {qbBacklogBusy ? 'Loading…' : 'Review'}
                                        </button>
                                    </div>
                                ) : ((qbSettingsBacklog.deferred?.count || 0) === 0 && (qbSettingsBacklog.skipped?.count || 0) === 0) ? (
                                    <p className="mt-1 text-xs text-gray-500">No earlier account credits are waiting to sync.</p>
                                ) : (
                                    /* creditbacklog-8b3d2f19 -- two labelled buckets. deferred = awaiting a
                                       decision (fuller summary + date range + double-count warning + Post +
                                       Don't sync these); skipped = carved off by a prior "from here forward",
                                       recoverable (count + warning + Sync to QuickBooks). Both actions route
                                       through the SAME setQuickBooksCreditBacklogPolicy callable: 'post'
                                       drains everything (un-skips skipped -> deferred), 'forwardOnly' carves
                                       the deferred set off to skipped. */
                                    <div className="mt-2 space-y-4">
                                        {(qbSettingsBacklog.deferred?.count || 0) > 0 && (
                                            <div>
                                                <p className="text-xs text-gray-700">
                                                    <span className="font-semibold">{qbSettingsBacklog.deferred.count}</span> earlier account
                                                    {qbSettingsBacklog.deferred.count === 1 ? ' credit' : ' credits'} totalling{' '}
                                                    <span className="font-semibold">{money(qbSettingsBacklog.deferred.totalValue)}</span>
                                                    {qbSettingsBacklog.deferred.earliest && qbSettingsBacklog.deferred.latest && (
                                                        <> , dated <span className="font-semibold">{qbSettingsBacklog.deferred.earliest}</span> to <span className="font-semibold">{qbSettingsBacklog.deferred.latest}</span></>
                                                    )} {qbSettingsBacklog.deferred.count === 1 ? 'has' : 'have'} not been sent to QuickBooks yet.
                                                </p>
                                                <p className="mt-2 text-xs text-amber-700 flex items-start gap-1">
                                                    <AlertCircle size={12} className="shrink-0 mt-0.5" />
                                                    Posting {qbSettingsBacklog.deferred.count === 1 ? 'it creates a new Payment' : `them creates ${qbSettingsBacklog.deferred.count} new Payments`} in
                                                    QuickBooks. Any of this money already entered there by hand will then appear twice, which Spatchy can't detect.
                                                </p>
                                                <div className="mt-3 flex gap-2">
                                                    <button
                                                        onClick={() => chooseBacklogPolicy('post')}
                                                        disabled={qbBacklogBusy}
                                                        className="px-3 py-1.5 bg-[#2ca01c] text-white text-xs font-bold rounded-lg hover:bg-[#238216] disabled:opacity-50"
                                                    >
                                                        {qbBacklogBusy ? 'Saving…' : 'Post to QuickBooks'}
                                                    </button>
                                                    <button
                                                        onClick={() => chooseBacklogPolicy('forwardOnly')}
                                                        disabled={qbBacklogBusy}
                                                        className="px-3 py-1.5 text-gray-700 text-xs font-medium border border-gray-200 hover:bg-gray-50 rounded-lg disabled:opacity-50"
                                                    >
                                                        {qbBacklogBusy ? 'Saving…' : "Don't sync these"}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {(qbSettingsBacklog.skipped?.count || 0) > 0 && (
                                            <div>
                                                <p className="text-xs text-gray-700">
                                                    <span className="font-semibold">{qbSettingsBacklog.skipped.count}</span> earlier account
                                                    {qbSettingsBacklog.skipped.count === 1 ? ' credit' : ' credits'} totalling{' '}
                                                    <span className="font-semibold">{money(qbSettingsBacklog.skipped.totalValue)}</span>
                                                    {qbSettingsBacklog.skipped.earliest && qbSettingsBacklog.skipped.latest && (
                                                        <> , dated <span className="font-semibold">{qbSettingsBacklog.skipped.earliest}</span> to <span className="font-semibold">{qbSettingsBacklog.skipped.latest}</span></>
                                                    )} {qbSettingsBacklog.skipped.count === 1 ? 'is' : 'are'} set to not sync. You can still send {qbSettingsBacklog.skipped.count === 1 ? 'it' : 'them'}.
                                                </p>
                                                <p className="mt-2 text-xs text-amber-700 flex items-start gap-1">
                                                    <AlertCircle size={12} className="shrink-0 mt-0.5" />
                                                    Syncing {qbSettingsBacklog.skipped.count === 1 ? 'it creates a new Payment' : `them creates ${qbSettingsBacklog.skipped.count} new Payments`} in
                                                    QuickBooks. Any of this money already entered there by hand will then appear twice, which Spatchy can't detect.
                                                </p>
                                                <div className="mt-3 flex gap-2">
                                                    <button
                                                        onClick={() => chooseBacklogPolicy('post')}
                                                        disabled={qbBacklogBusy}
                                                        className="px-3 py-1.5 bg-[#2ca01c] text-white text-xs font-bold rounded-lg hover:bg-[#238216] disabled:opacity-50"
                                                    >
                                                        {qbBacklogBusy ? 'Saving…' : 'Sync to QuickBooks'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        <button
                                            onClick={() => setQbSettingsBacklog(null)}
                                            disabled={qbBacklogBusy}
                                            className="px-3 py-1.5 text-gray-700 text-xs font-medium border border-gray-200 hover:bg-gray-50 rounded-lg disabled:opacity-50"
                                        >
                                            Close
                                        </button>
                                    </div>
                                )}
                            </div>

                            {isLoadingOptions ? (
                                <div className="flex items-center gap-2 text-gray-500 text-sm py-4">
                                    <Loader2 className="animate-spin" size={16} /> Loading QuickBooks Accounts...
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* 1. Default SERVICE Income Account */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Default Service Account (Required)
                                            </label>
                                            <select
                                                className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:border-green-500 focus:ring-green-500"
                                                value={qbSettings.incomeAccountId}
                                                onChange={(e) => setQbSettings({ ...qbSettings, incomeAccountId: e.target.value })}
                                            >
                                                <option value="">-- Select Service Income --</option>
                                                {qbOptions.accounts.map(acc => (
                                                    <option key={acc.Id} value={acc.Id}>
                                                        {acc.FullyQualifiedName || acc.Name}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">For Labor/Services (e.g., Service Income)</p>
                                        </div>

                                        {/* 2. Default MATERIAL/GOODS Income Account */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Default Material/Good Account (Required)
                                            </label>
                                            <select
                                                className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:border-green-500 focus:ring-green-500"
                                                value={qbSettings.materialAccountId}
                                                onChange={(e) => setQbSettings({ ...qbSettings, materialAccountId: e.target.value })}
                                            >
                                                <option value="">-- Select Material Income --</option>
                                                {qbOptions.accounts.map(acc => (
                                                    <option key={acc.Id} value={acc.Id}>
                                                        {acc.FullyQualifiedName || acc.Name}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">For Parts/Goods (e.g., Sales of Product Income)</p>
                                        </div>
                                    </div>

                                    {/* d2refsel-2f8a6b4d -- Seam D2: refund-from bank account.
                                        The account QuickBooks pays deposit refunds from. Its list
                                        (qbBankAccounts) is already filtered to type === 'Bank' at
                                        fetch time, so this select can NEVER offer Undeposited Funds
                                        or any Other Current Asset / Credit Card account. Optional:
                                        an org that never refunds can leave it "-- No account set --"
                                        and still save. Seeded from and written into the same
                                        quickbooksSettings map, so saving preserves income/material/tax. */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Refund-from Bank Account (Optional)
                                        </label>
                                        <select
                                            className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:border-green-500 focus:ring-green-500"
                                            value={qbSettings.refundFromAccountId}
                                            onChange={(e) => setQbSettings({ ...qbSettings, refundFromAccountId: e.target.value })}
                                        >
                                            <option value="">-- No account set --</option>
                                            {qbBankAccounts.map(acc => (
                                                <option key={acc.id} value={acc.id}>
                                                    {acc.name}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-gray-500 mt-1">
                                            The bank account QuickBooks pays deposit refunds from — choose the account your deposits are deposited into. Needed only so a refunded deposit reverses automatically in QuickBooks.
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* 3. Tax Code */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Default Tax Code <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:border-green-500 focus:ring-green-500"
                                                value={qbSettings.taxCodeId}
                                                onChange={(e) => setQbSettings({ ...qbSettings, taxCodeId: e.target.value })}
                                            >
                                                <option value="">-- Select a tax code --</option>
                                                {qbOptions.taxCodes.map(code => (
                                                    <option key={code.Id} value={code.Id}>
                                                        {code.Name}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">Required. Used for any invoice line that has no tax code of its own.</p>
                                        </div>

                                        {/* qbuntaxed-8d3f5b27 -- the code an invoice with no tax is sent on. Only
                                            QuickBooks codes with a 0% rate are offered. */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Code for untaxed invoices
                                            </label>
                                            <select
                                                className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:border-green-500 focus:ring-green-500"
                                                value={qbSettings.untaxedTaxCodeId}
                                                onChange={(e) => setQbSettings({ ...qbSettings, untaxedTaxCodeId: e.target.value })}
                                            >
                                                <option value="">-- Not chosen --</option>
                                                {qbOptions.taxCodes.filter(code => Number(code.Rate) === 0).map(code => (
                                                    <option key={code.Id} value={code.Id}>
                                                        {code.Name}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">Used when an invoice has no tax, and for lines ticked Tax Exempt. Until one is chosen, an invoice with no tax can't be synced. Ask your accountant whether this should be Exempt or Zero-rated.</p>
                                        </div>
                                        
                                        {/* Save Button */}
                                        <div className="flex items-end justify-end md:col-span-2">
                                            <button
                                                onClick={handleSaveQBSettings}
                                                disabled={isSavingSettings || !qbSettings.incomeAccountId || !qbSettings.materialAccountId || !qbSettings.taxCodeId}
                                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
                                            >
                                                {isSavingSettings ? (
                                                    <><Loader2 className="animate-spin" size={16} /> Saving...</>
                                                ) : (
                                                    <><Save size={16} /> Save Settings</>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* qbsync-5e1a7c34 -- Seam A: automatic invoice sync timing.
                                        A bare top-level org enum (DepositSettings precedent), written
                                        straight to the org doc and read live off the companySettings
                                        snapshot -- no local state. The two automatic options are
                                        disabled until accounting accounts are configured: choosing
                                        them without those set would make every invoice silently fail
                                        to sync (the exact pile-up this feature exists to prevent). */}
                                    <div className="border-t border-green-200 pt-4">
                                        <h4 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2">
                                            <Calculator size={16} /> Automatic Invoice Sync
                                        </h4>
                                        <p className="text-xs text-gray-500 mb-3">
                                            Choose when invoices flow to QuickBooks. Invoices imported from QuickBooks are never auto-synced.
                                        </p>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            When should invoices sync to QuickBooks?
                                        </label>
                                        <select
                                            className="w-full md:w-1/2 border-gray-300 rounded-md shadow-sm text-sm focus:border-green-500 focus:ring-green-500 disabled:opacity-60"
                                            value={qbSyncTiming}
                                            onChange={(e) => handleSyncTimingChange(e.target.value)}
                                        >
                                            <option value="manual">Manual only</option>
                                            <option value="on_send" disabled={!qbAccountsConfigured}>When an invoice is emailed</option>
                                            <option value="on_create" disabled={!qbAccountsConfigured}>When an invoice is created</option>
                                        </select>
                                        <p className="text-xs text-gray-500 mt-2">{QB_SYNC_TIMING_COPY[qbSyncTiming]}</p>
                                        {!qbAccountsConfigured && (
                                            <p className="text-xs text-amber-600 flex items-start gap-1 mt-2">
                                                <AlertCircle size={12} className="shrink-0 mt-0.5" />
                                                Set a Default Service and Default Material account above and save before you can turn on automatic sync. Until then only Manual is available — otherwise every invoice would silently fail to sync.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

            </div>
            {message && (
                <div className="mt-6 p-4 bg-blue-50 text-blue-700 text-sm rounded-md border border-blue-200">
                    {message}
                </div>
            )}
        </div>
    );
};

export default IntegrationsPage;